<template>
  <div class="inspection-report-detail">
    <div class="report-header">
      <p class="report-type">{{ reportTypeName }}</p>
      <h1>{{ reportTitle }}</h1>
      <p class="report-range" v-if="rangeText">巡查时间：{{ rangeText }}</p>
      <p class="report-time" v-if="generateTime">生成时间：{{ generateTime }}</p>
    </div>

    <div v-if="loading" class="page-state">报告加载中...</div>
    <div v-else-if="errorMessage" class="page-state error-state">{{ errorMessage }}</div>

    <template v-else>
      <!-- 一、巡查概览 -->
      <section class="report-section">
        <h2>一、总体巡查结果</h2>
        <div class="stat-grid">
          <div v-for="item in overviewStats" :key="item.label" class="stat-item">
            <strong>{{ item.value }}</strong>
            <span>{{ item.label }}</span>
          </div>
        </div>
        <div class="summary-list">
          <p v-for="(text, index) in overviewSummary" :key="index" class="summary-line">{{ text }}</p>
        </div>
      </section>

      <!-- 二、政务媒体巡查 -->
      <section v-if="showGovSection" class="report-section">
        <h2>{{ govSectionNumber }}、{{ mediaSectionTitle }}</h2>

        <!-- 涉政不规范表述 -->
        <div v-if="isEnabled(szbgf.check)" class="sub-block">
          <h3 class="sub-title">涉政不规范表述</h3>
          <p v-if="!hasViolationData" class="summary-line">{{ violationEmptyText }}</p>
          <template v-else>
            <p class="summary-line">{{ violationSummary }}</p>

          <template v-if="trendChartData">
            <p class="table-caption">不规范表述趋势分析</p>
            <div ref="trendChart" class="chart-box"></div>
          </template>

          <template v-if="sourceChartData">
            <p class="table-caption">不规范文章来源分布</p>
            <div ref="sourceChart" class="chart-box"></div>
          </template>

          <p v-if="sourceSummary" class="summary-line">{{ sourceSummary }}</p>

          <p v-if="classificationSummary" class="summary-line">{{ classificationSummary }}</p>

          <template v-if="classifyChartData">
            <p class="table-caption">不规范表述词分类</p>
            <div ref="classifyChart" class="chart-box"></div>
          </template>

          <template v-if="highFreqChartData">
            <p class="table-caption">高频不规范词分析</p>
            <div ref="highFreqChart" class="chart-box"></div>
          </template>

            <p v-if="highFreqSummary" class="summary-line">{{ highFreqSummary }}</p>
          </template>
        </div>

        <!-- 含敏感信息文章 -->
        <div v-if="isEnabled(sensitive.check)" class="sub-block">
          <h3 class="sub-title">{{ isGovCustomer ? "含敏感信息文章" : "敏感信息巡查" }}</h3>
          <p class="summary-line">{{ sensitiveSummary }}</p>
          <template v-if="hasSensitiveData">
            <div v-for="(ex, i) in sensitiveExamples" :key="'sx' + i" class="example-card">
              <p class="example-title">{{ ex.title || '示例文章' }}</p>
              <p class="example-meta">
                <span v-if="ex.siteName">{{ ex.siteName }}</span>
                <span v-if="ex.publishTime">{{ ex.publishTime }}</span>
              </p>
              <p v-if="ex.siteType" class="example-meta">媒体类型：{{ ex.siteType }}</p>
              <p v-if="ex.word || ex.errorWords" class="example-word">命中：{{ ex.word || ex.errorWords }}</p>
              <p v-if="ex.sxw" class="example-word">所在上下文：{{ ex.sxw }}</p>
              <a v-if="ex.url" class="example-link" :href="ex.url" target="_blank">查看原文</a>
            </div>
          </template>
        </div>

        <!-- 僵尸信源 -->
        <div v-if="isEnabled(zombie.check)" class="sub-block">
          <h3 class="sub-title">僵尸信源</h3>
          <p class="summary-line">{{ zombieSummary }}</p>
          <div v-if="hasZombieData && zombieRows.length" class="table-wrap">
            <table>
              <thead>
                <tr><th>序号</th><th>媒体平台</th><th>信源名称</th><th>主办单位性质</th><th>主页/域名</th><th>作者ID/ICP</th><th>最后发布时间</th><th>未更新天数</th></tr>
              </thead>
              <tbody>
                <tr v-for="(row, i) in zombieRows" :key="'zb' + i">
                  <td>{{ i + 1 }}</td>
                  <td>{{ row.siteType || '-' }}</td>
                  <td class="break-word">{{ row.siteName || '-' }}</td>
                  <td>{{ row.unitNatureName || '-' }}</td>
                  <td class="break-word">{{ row.homePageLink || row.siteDomain || '-' }}</td>
                  <td class="break-word">{{ row.sourceId || row.icp || '-' }}</td>
                  <td>{{ row.publishTime || '-' }}</td>
                  <td>{{ num(row.days) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <!-- 委办局模板中的不良网站属于“媒体巡查”，不单独展示属地媒体章节 -->
        <div v-if="!isGovCustomer && isEnabled(badWebsite.check)" class="sub-block">
          <h3 class="sub-title">不良网站</h3>
          <p class="summary-line">{{ badWebsiteSummary }}</p>
        </div>
      </section>

      <!-- 属地媒体巡查（仅网信/宣传类账号展示） -->
      <section v-if="showLocalSection" class="report-section">
        <h2>{{ localSectionNumber }}、属地媒体巡检</h2>

        <!-- 涉政有害信息 -->
        <div v-if="isEnabled(harmful.check)" class="sub-block">
          <h3 class="sub-title">涉政有害信息</h3>
          <p class="summary-line">{{ harmfulSummary }}</p>
          <div v-for="(row, i) in harmfulExamples" :key="'hm' + i" class="example-card">
            <p class="example-title">{{ row.title || '示例文章' }}</p>
            <p class="example-meta">
              <span v-if="row.siteName || row.source">{{ row.siteName || row.source }}</span>
              <span v-if="row.publishTime">{{ row.publishTime }}</span>
            </p>
            <p v-if="row.siteTypeDesc" class="example-meta">媒体类型：{{ row.siteTypeDesc }}</p>
            <p v-if="row.copyProblemWord" class="example-word">命中：{{ row.copyProblemWord }}</p>
            <p v-if="row.matchContext" class="example-word">所在上下文：{{ row.matchContext }}</p>
            <a v-if="row.url" class="example-link" :href="row.url" target="_blank">查看原文</a>
          </div>
        </div>

        <!-- 违法违规信息 -->
        <div v-if="isEnabled(illegal.check)" class="sub-block">
          <h3 class="sub-title">违法违规信息</h3>
          <p class="summary-line">{{ illegalSummary }}</p>
          <div v-for="(row, i) in illegalExamples" :key="'il' + i" class="example-card">
            <p class="example-title">{{ row.title || '示例文章' }}</p>
            <p class="example-meta">
              <span v-if="row.siteName || row.source">{{ row.siteName || row.source }}</span>
              <span v-if="row.publishTime">{{ row.publishTime }}</span>
            </p>
            <p v-if="row.siteTypeDesc" class="example-meta">媒体类型：{{ row.siteTypeDesc }}</p>
            <p v-if="row.copyProblemWord" class="example-word">命中：{{ row.copyProblemWord }}</p>
            <p v-if="row.matchContext" class="example-word">所在上下文：{{ row.matchContext }}</p>
            <a v-if="row.url" class="example-link" :href="row.url" target="_blank">查看原文</a>
          </div>
        </div>

        <!-- 属地不良网站 -->
        <div v-if="isEnabled(badWebsite.check)" class="sub-block">
          <h3 class="sub-title">属地不良网站</h3>
          <p class="summary-line">{{ badWebsiteSummary }}</p>
        </div>
      </section>

      <!-- 专项行动 -->
      <section v-if="showSpecialSection" class="report-section">
        <h2>{{ specialSectionNumber }}、专项行动</h2>
        <p class="summary-line">{{ specialActionSummary }}</p>
        <div v-if="specialActions.length" class="table-wrap">
          <table>
            <thead>
              <tr><th>序号</th><th>专项名称</th><th>巡查状态</th><th>数量</th></tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in specialActions" :key="'sa' + index">
                <td>{{ index + 1 }}</td>
                <td class="break-word">{{ item.name || '-' }}</td>
                <td>{{ item.status || '-' }}</td>
                <td>{{ item.total == null ? '-' : item.total }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import moment from "moment";
import * as echarts from "echarts";
import { Toast } from "vant";
import {
  getWechatCode,
  getInspectionReportDetail,
} from "@/api/inspectionReportDetail";

export default {
  name: "inspectionReportDetail",
  data() {
    return {
      loading: true,
      errorMessage: "",
      reportId: "",
      openId: "",
      reportType: null,
      govCustomer: null,
      accountRegion: "",
      startTime: "",
      endTime: "",
      displayEndTime: "",
      generateReportTime: "",
      overview: {},
      gov: {},
      local: {},
      specialAction: {},
    };
  },
  computed: {
    reportTypeName() {
      const names = { 1: "周报", 2: "月报", 3: "季报", 4: "年报", 10: "自助报告" };
      return names[this.reportType] || "巡查报告";
    },
    reportTitle() {
      return (this.isGovCustomer ? "网络内容监管" : "网络内容安全巡查") + this.reportTypeName;
    },
    rangeText() {
      const endTime = this.displayEndTime || this.endTime;
      if (!this.startTime || !endTime) return "";
      return this.formatDate(this.startTime) + " 至 " + this.formatDate(endTime);
    },
    isGovCustomer() {
      return this.govCustomer === 1;
    },
    showGovSection() {
      const govEnabled = [this.szbgf, this.sensitive, this.zombie]
        .some((item) => this.isEnabled(item.check));
      return govEnabled || (!this.isGovCustomer && this.isEnabled(this.badWebsite.check));
    },
    showLocalSection() {
      return this.isGovCustomer &&
        [this.harmful, this.illegal, this.badWebsite].some((item) => this.isEnabled(item.check));
    },
    showSpecialSection() {
      return this.isEnabled(this.specialAction.check);
    },
    govSectionNumber() {
      return 2;
    },
    localSectionNumber() {
      return this.showGovSection ? 3 : 2;
    },
    specialSectionNumber() {
      return 2 + (this.showGovSection ? 1 : 0) + (this.showLocalSection ? 1 : 0);
    },
    mediaSectionTitle() {
      return this.isGovCustomer ? "政务媒体巡查" : "媒体巡查";
    },
    generateTime() {
      return this.generateReportTime ? this.formatDateTime(this.generateReportTime) : "";
    },
    overviewStats() {
      const o = this.overview;
      if (!this.isGovCustomer) {
        return [
          { label: "信源总量", value: this.num(o.xyzl) },
          { label: "网站", value: this.num(o.zwwz) + this.num(o.sdwz) },
          { label: "新媒体账号", value: this.num(o.zwxmt) + this.num(o.sdxmt) },
          { label: "全部文章", value: this.num(o.allArticle) },
          { label: "不规范表述文章", value: this.num(o.voilationNews) },
          { label: "敏感信息文章", value: this.num(o.sensitiveNews) },
        ];
      }
      return [
        { label: "信源总量", value: this.num(o.xyzl) },
        { label: "政务网站", value: this.num(o.zwwz) },
        { label: "政务新媒体", value: this.num(o.zwxmt) },
        { label: "属地网站", value: this.num(o.sdwz) },
        { label: "属地新媒体", value: this.num(o.sdxmt) },
        { label: "全部文章", value: this.num(o.allArticle) },
        { label: "敏感信息文章", value: this.num(o.sensitiveNews) },
        { label: "涉政有害信息", value: this.num(o.politicalNews) },
        { label: "违法违规信息", value: this.num(o.lllegalNews) },
      ];
    },
    overviewSummary() {
      const o = this.overview;
      const wlmt = this.num(o.wlmt);
      const webScan = this.num(o.webScan);
      const zombieSource = this.num(o.zombieSource);
      const zombieDay = this.num(o.zombieDay);
      const list = [];
      if (wlmt === 0) {
        list.push("未发现新增的网络媒体。");
      } else {
        list.push(
          "新增网络媒体" + wlmt + "个，其中网站" + this.num(o.wz) + "个，微信公众号" +
          this.num(o.wechat) + "个，微博" + this.num(o.weibo) + "个，短视频" +
          this.num(o.video) + "个，今日头条" + this.num(o.toutiao) + "个，百家号" +
          this.num(o.baijia) + "个，网易号" + this.num(o.wangyi) + "个。"
        );
      }
      list.push(
        webScan === 0
          ? "未发现不良、无法打开的网站。"
          : "发现" + (this.isGovCustomer ? "属地" : "") +
            "不良、无法打开的网站" + webScan + "个。"
      );
      list.push(
        zombieSource === 0
          ? "未发现超过" + zombieDay + "天未更新的僵尸信源。"
          : "超过" + zombieDay + "天未更新的僵尸信源" + zombieSource + "个。"
      );
      return list;
    },
    // 涉政不规范表述
    szbgf() {
      return this.gov.szbgf || {};
    },
    hasViolationData() {
      return this.num(this.szbgf.voilationNews) > 0;
    },
    violationEmptyText() {
      const region = this.accountRegion ? "涉及" + this.accountRegion + "的" : "";
      const mediaName = this.isGovCustomer ? "政务媒体" : "官方媒体";
      return "巡查期间，" + region + mediaName +
        "发表文章中，未发现涉政表述不规范文章，请继续保持。";
    },
    violationSummary() {
      const s = this.szbgf;
      const classify = s.bgfcfl || {};
      const platforms = this.arr(s.ptTop3).join("、") || "相关";
      const prefix = this.accountRegion ? "涉及" + this.accountRegion + "的" : "";
      const articleText = this.isGovCustomer
        ? ""
        : "共巡查文章" + this.num(classify.allArticle) + "篇，其中";
      return "巡查期间，" + prefix + (this.isGovCustomer ? "政务媒体" : "官方媒体") +
        "所发表文章中，" + articleText + "涉政不规范表述文章" +
        this.num(s.voilationNews) + "篇，涉及" + this.num(s.szbgfpt) +
        "类平台账号，主要来源于" + platforms + "平台。其中，严重错敏文章" +
        this.num(s.yz) + "篇，已处置" + this.num(s.yzChu) + "篇；一般错敏文章" +
        this.num(s.yb) + "篇，已处置" + this.num(s.ybChu) + "篇；轻微错敏文章" +
        this.num(s.qw) + "篇，已处置" + this.num(s.qwChu) + "篇。";
    },
    szbgfSources() {
      return this.arr((this.szbgf.bgfwzlyfb || {}).bgflyzb);
    },
    sourceSummary() {
      const rows = this.szbgfSources;
      if (!rows.length) return "";
      let str = "";
      rows.forEach((row, i) => {
        const site = row.siteType ? row.siteType + "-" : "";
        if (i === 0) {
          str += "不规范表述文章来源最多的" + site + (row.name || "") +
            "，不规范表述文章最多达到" + this.num(row.count) + "篇，占比" + this.pctText(row.percentage);
        } else {
          str += "、" + site + (row.name || "") + this.num(row.count) + "篇，占比" + this.pctText(row.percentage);
        }
      });
      return str + "。";
    },
    szbgfClassify() {
      return this.arr((this.szbgf.bgfcfl || {}).bgfcflzb);
    },
    classificationSummary() {
      const classify = this.szbgf.bgfcfl || {};
      const details = this.szbgfClassify
        .map((item) =>
          (item.name || "-") + this.num(item.count) + "次，占比" + this.pctText(item.percentage)
        )
        .join("、");
      return "巡查期间，共巡查文章" + this.num(classify.allArticle) +
        "篇，其中含不规范表述的文章" + this.num(classify.voilationNews) +
        "篇，涉及" + this.num(classify.voilationWords) + "个不规范表述词" +
        (details ? "，分类主要集中在" + details : "") + "。";
    },
    szbgfHighFreq() {
      return this.arr((this.szbgf.gpbgfcfx || {}).top);
    },
    highFreqSummary() {
      const top = this.szbgfHighFreq;
      if (!top.length) return "未发现不规范表述词";
      let str = "";
      const mid = [];
      top.forEach((w, i) => {
        if (i === 0) str += "“" + w + "”出错频率最高";
        else if (i === 1) str += "、“" + w + "”频率次之";
        else mid.push("“" + w + "”");
      });
      if (mid.length) str += "，" + mid.join("、") + "等词错误频率较高";
      return str;
    },
    // 图表数据（与在线预览同源：reportResult 里的 ReportCharEntity）
    trendChartData() {
      return this.toChartData(this.szbgf.voilationTrendChart);
    },
    sourceChartData() {
      return this.toChartData((this.szbgf.bgfwzlyfb || {}).voilationNewsTop10Chart);
    },
    classifyChartData() {
      return this.toChartData((this.szbgf.bgfcfl || {}).bgfcflChart);
    },
    highFreqChartData() {
      return this.toChartData((this.szbgf.gpbgfcfx || {}).gpbgfcfxChart);
    },
    // 含敏感信息文章
    sensitive() {
      return this.gov.hmgxxwz || {};
    },
    hasSensitiveData() {
      const yssj = this.sensitive.yssj || {};
      const lmgy = this.sensitive.lmgy || {};
      return this.num(yssj.yssjsl) > 0 || this.num(lmgy.lmgysl) > 0;
    },
    sensitiveSummary() {
      const yssj = this.sensitive.yssj || {};
      const lmgy = this.sensitive.lmgy || {};
      const privacyCount = this.num(yssj.yssjsl);
      const officialCount = this.num(lmgy.lmgysl);
      if (privacyCount === 0 && officialCount === 0) {
        return "系统未发现官方媒体中发布中疑似泄露公民身份证号的文章。未发现提及敏感官员的正面宣传文章。";
      }
      const privacyText = privacyCount === 0
        ? "系统未发现官方媒体中发布了疑似泄露公民身份证号码的文章。"
        : "巡查期间，除涉政不规范表述错误外，网媒管理系统还巡检到官方媒体发布文章中疑似泄露公民身份证号的文章" +
          privacyCount + "篇、涉及" + this.num(yssj.yssjmt) + "个媒体账号。";
      const officialText = officialCount === 0
        ? "未发现提及敏感官员的正面宣传文章。"
        : "提及敏感官员正面宣传文章" + officialCount +
          "篇，涉及" + this.num(lmgy.lmgymt) + "个媒体账号。";
      return privacyText + officialText;
    },
    sensitiveExamples() {
      const list = [];
      const yssjData = this.sensitive.yssj || {};
      const lmgyData = this.sensitive.lmgy || {};
      const yssj = yssjData.slwz;
      const lmgy = lmgyData.slwz;
      if (this.num(yssjData.yssjsl) > 0 && yssj && yssj.title) list.push(yssj);
      if (this.num(lmgyData.lmgysl) > 0 && lmgy && lmgy.title) list.push(lmgy);
      return list;
    },
    // 僵尸信源
    zombie() {
      return this.gov.jsxy || {};
    },
    hasZombieData() {
      return this.num(this.zombie.zombieSource) > 0;
    },
    zombieSummary() {
      if (!this.hasZombieData) {
        return "巡查期间未发现超过" + this.zombieDay + "天未发文的僵尸信源。";
      }
      return "巡查期间发现超过" + this.zombieDay + "天未发文的僵尸信源" +
        this.num(this.zombie.zombieSource) + "个。不活跃的前" +
        Math.min(this.num(this.zombie.zombieSource), 10) + "个僵尸信源清单如下：";
    },
    zombieDay() {
      const value = this.zombie.dayRange;
      if (value !== null && value !== undefined && value !== "") {
        const dayRange = Number(value);
        if (!isNaN(dayRange)) return dayRange;
      }
      return this.num(this.overview.zombieDay);
    },
    zombieRows() {
      return this.arr(this.zombie.zombieTab);
    },
    // 涉政有害信息
    harmful() {
      return this.local.szyhxx || {};
    },
    hasHarmfulData() {
      return this.num(this.harmful.szyhxxsl) > 0 || this.num(this.harmful.smzzjxxsl) > 0;
    },
    harmfulSummary() {
      const politics = this.num(this.harmful.szyhxxsl);
      const religion = this.num(this.harmful.smzzjxxsl);
      return (politics === 0 ? "未发现涉政信息" : "监测发现涉政信息" + politics + "条") +
        (religion === 0 ? "、未发现涉民族宗教类信息。" : "、涉民族宗教类信息" + religion + "条。");
    },
    harmfulExamples() {
      return this.hasHarmfulData ? this.arr(this.harmful.slwz).slice(0, 2) : [];
    },
    // 违法违规信息
    illegal() {
      return this.local.wfwgxx || {};
    },
    hasIllegalData() {
      return this.num(this.illegal.shxxsl) > 0 || this.num(this.illegal.sdxxsl) > 0 ||
        this.num(this.illegal.sbkxxsl) > 0;
    },
    illegalSummary() {
      const found = [];
      const missing = [];
      const definitions = [
        { name: "涉黄", count: this.num(this.illegal.shxxsl) },
        { name: "涉赌", count: this.num(this.illegal.sdxxsl) },
        { name: "涉暴恐", count: this.num(this.illegal.sbkxxsl) },
      ];
      definitions.forEach((item) => {
        if (item.count > 0) found.push("疑似" + item.name + "信息" + item.count + "条");
        else missing.push(item.name);
      });
      const foundText = found.length ? "监测发现" + found.join("，") + "。" : "";
      const missingText = missing.length ? "未发现" + missing.join("、") + "信息。" : "";
      return foundText + missingText;
    },
    illegalExamples() {
      const categoryCount = [this.illegal.shxxsl, this.illegal.sdxxsl, this.illegal.sbkxxsl]
        .filter((count) => this.num(count) > 0).length;
      return this.hasIllegalData ? this.arr(this.illegal.slwz).slice(0, categoryCount) : [];
    },
    // 属地不良网站
    badWebsite() {
      return this.local.sdblwz || {};
    },
    badWebsiteSummary() {
      const prefix = this.isGovCustomer ? "属地" : "";
      const definitions = [
        { name: "涉黄", data: this.badWebsite.shWeb || {} },
        { name: "涉赌", data: this.badWebsite.sdWeb || {} },
        { name: "无法打开", data: this.badWebsite.wfdkWeb || {} },
      ];
      const found = [];
      const missing = [];
      definitions.forEach((item) => {
        const total = this.num(item.data.total);
        if (total > 0) {
          const example = item.data.name
            ? "“" + item.data.name + (item.data.domain ? "（" + item.data.domain + "）" : "") + "”等"
            : "";
          found.push(example + (item.name === "无法打开" ? "无法打开网站" : "疑似" + item.name + "网站") + total + "个");
        } else {
          missing.push(item.name);
        }
      });
      if (!found.length) {
        return "巡查期间，未发现" + prefix + "涉黄、涉赌、无法打开等不良网站。";
      }
      return "巡查期间，发现" + prefix + "：" + found.join("、") + "。" +
        (missing.length ? "未发现" + prefix + missing.join("、") + "等不良网站。" : "");
    },
    specialActions() {
      if (!this.arr(this.specialAction.specialActionNameList).length) return [];
      return this.arr(this.specialAction.tables).map((item) => ({
        name: item.actionName || item.name || "-",
        total: item.count == null ? item.total : item.count,
        status: this.specialActionStatus(item),
      }));
    },
    specialActionSummary() {
      const names = this.arr(this.specialAction.specialActionNameList);
      if (!names.length) return "巡查期间，未设置专项行动监测方案。";
      return "巡查期间，积极开展属地专项行动，设置属地专项行动" + names.length +
        "个：" + names.map((name) => "“" + name + "”").join("") + "，巡查效果如下：";
    },
  },
  created() {
    const icon = document.querySelector("link.linkico");
    if (icon) icon.href = "./wmico.ico";
    this.charts = [];
  },
  mounted() {
    this.initPage();
    this.resizeHandler = () => this.charts.forEach((c) => c && c.resize());
    window.addEventListener("resize", this.resizeHandler);
  },
  beforeDestroy() {
    if (this.resizeHandler) window.removeEventListener("resize", this.resizeHandler);
    this.disposeCharts();
  },
  methods: {
    initPage() {
      const query = this.$route.query || {};
      // 预警推送的 OAuth state 形如 {reportId}_{accountId}，reportId 为 ULID（不含下划线）。
      this.reportId = query.reportId || query.id || this.parseReportIdFromState(query.state);
      this.openId =
        query.openId || query.openid || localStorage.getItem("wlgl-openId") || "";

      if (!this.reportId) {
        this.loading = false;
        this.errorMessage = "缺少报告ID，无法查看报告";
        return;
      }
      // 微信回调 code/state 优先于本地 Token，避免复用已过期的隔夜 Token。
      if (query.code && query.state) {
        this.queryCode(query.code, query.state);
        return;
      }
      if (localStorage.getItem("wlgl-token")) {
        if (this.openId) localStorage.setItem("wlgl-openId", this.openId);
        this.loadDetail();
        return;
      }
      this.loading = false;
      this.errorMessage = "登录信息已失效，请从微信预警消息重新进入";
    },
    queryCode(code, state) {
      Toast.loading({ message: "加载中...", forbidClick: true, duration: 0 });
      getWechatCode({ code, state })
        .then((res) => {
          Toast.clear();
          const body = res.data || {};
          if (body.code !== 200 || !body.data) {
            this.loading = false;
            this.errorMessage = body.message || "登录失败";
            return;
          }
          localStorage.setItem("wlgl-token", body.data);
          if (body.openId) {
            this.openId = body.openId;
            localStorage.setItem("wlgl-openId", body.openId);
          }
          // OAuth code 只能短时使用，兑换成功后从地址栏移除，避免刷新时重复兑换。
          const nextQuery = Object.assign({}, this.$route.query, {
            reportId: this.reportId,
          });
          delete nextQuery.code;
          delete nextQuery.state;
          this.$router.replace(
            { path: this.$route.path, query: nextQuery },
            () => {},
            () => {}
          );
          this.loadDetail();
        })
        .catch(() => {
          Toast.clear();
          localStorage.removeItem("wlgl-token");
          this.loading = false;
          this.errorMessage = "登录失败，请稍后重试";
        });
    },
    loadDetail() {
      this.loading = true;
      this.errorMessage = "";
      getInspectionReportDetail({ reportId: this.reportId })
        .then((res) => {
          this.loading = false;
          const body = res.data || {};
          if (body.code === 20009) {
            localStorage.removeItem("wlgl-token");
            localStorage.removeItem("wlgl-openId");
            this.errorMessage = "登录信息已失效，请从微信预警消息重新进入";
            return;
          }
          if (body.code !== 200 || !body.data) {
            this.errorMessage = body.message || "获取报告详情失败";
            return;
          }
          const data = body.data;
          this.reportType = data.reportType;
          this.govCustomer = Number(data.govCustomer);
          this.accountRegion = data.accountRegion || "";
          this.startTime = data.startTime;
          this.endTime = data.endTime;
          this.displayEndTime = data.displayEndTime || data.endTime;
          this.generateReportTime = data.generateReportTime;
          const result = data.reportResult || {};
          this.overview = result.ztxcjg || {};
          this.gov = result.zwmtxc || {};
          this.local = result.sdmtxc || {};
          this.specialAction = result.zxxd || {};
          this.$nextTick(() => this.renderCharts());
        })
        .catch((error) => {
          this.loading = false;
          const message =
            error && error.response && error.response.data
              ? error.response.data.message
              : "网络异常，请稍后重试";
          this.errorMessage = message || "网络异常，请稍后重试";
        });
    },
    parseReportIdFromState(state) {
      if (!state) return "";
      const idx = String(state).lastIndexOf("_");
      return idx > -1 ? String(state).substring(0, idx) : String(state);
    },
    specialActionStatus(item) {
      const count = this.num(item.count == null ? item.total : item.count);
      const realTimeStatus = item.realTimeStatus == null ? -1 : Number(item.realTimeStatus);
      const historyStatus = item.historyStatus == null ? -1 : Number(item.historyStatus);
      if (count > 10000) return "已结束";
      if (realTimeStatus === 0) return "未开始";
      if (realTimeStatus === 2 || historyStatus === 2 ||
          (realTimeStatus === 1 && historyStatus === 0)) return "巡查中";
      if (realTimeStatus === 1 && historyStatus === 1) return "已结束";
      return "-";
    },
    isEnabled(check) {
      return Number(check) === 1;
    },
    num(value) {
      const n = Number(value);
      return isNaN(n) ? 0 : n;
    },
    arr(value) {
      return Array.isArray(value) ? value : [];
    },
    // 把后端 ReportCharEntity 转成 echarts 需要的数据；无有效数据返回 null
    toChartData(entity) {
      if (!entity) return null;
      const categories = this.arr(entity.charTitleValue).map((v) => String(v));
      const series = this.arr(entity.series);
      const first = series.length ? series[0] : null;
      const values = first ? this.arr(first.value).map((v) => this.num(v)) : [];
      if (!categories.length || !values.length) return null;
      const hasPositive = values.some((v) => v > 0);
      if (!hasPositive) return null;
      return {
        title: entity.charTitle || "",
        name: (first && first.name) || "数量",
        categories: categories,
        values: values,
      };
    },
    disposeCharts() {
      (this.charts || []).forEach((c) => c && c.dispose());
      this.charts = [];
    },
    renderCharts() {
      this.disposeCharts();
      this.initChart(this.$refs.trendChart, this.trendChartData, "line");
      this.initChart(this.$refs.sourceChart, this.sourceChartData, "barH");
      this.initChart(this.$refs.classifyChart, this.classifyChartData, "barH");
      this.initChart(this.$refs.highFreqChart, this.highFreqChartData, "barH");
    },
    initChart(dom, data, type) {
      if (!dom || !data) return;
      const chart = echarts.init(dom);
      chart.setOption(type === "line" ? this.lineOption(data) : this.barHOption(data));
      this.charts.push(chart);
    },
    lineOption(data) {
      return {
        color: ["#3d78d8"],
        grid: { left: 48, right: 24, top: 30, bottom: 56 },
        tooltip: { trigger: "axis" },
        xAxis: {
          type: "category",
          data: data.categories,
          axisLabel: { fontSize: 11, rotate: data.categories.length > 6 ? 35 : 0 },
        },
        yAxis: { type: "value", minInterval: 1 },
        series: [
          {
            name: data.name,
            type: "line",
            smooth: true,
            areaStyle: { opacity: 0.12 },
            data: data.values,
          },
        ],
      };
    },
    barHOption(data) {
      // 横向柱状：名称可能较长，倒序让最大值在顶部
      const categories = data.categories.slice().reverse();
      const values = data.values.slice().reverse();
      return {
        color: ["#3d78d8"],
        grid: { left: 8, right: 40, top: 16, bottom: 16, containLabel: true },
        tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
        xAxis: { type: "value", minInterval: 1 },
        yAxis: {
          type: "category",
          data: categories,
          axisLabel: { fontSize: 11, width: 140, overflow: "truncate" },
        },
        series: [
          {
            name: data.name,
            type: "bar",
            barMaxWidth: 18,
            label: { show: true, position: "right", fontSize: 11 },
            data: values,
          },
        ],
      };
    },
    pctText(value) {
      if (value == null || value === "") return "-";
      const str = String(value);
      return str.indexOf("%") > -1 ? str : str + "%";
    },
    formatDate(value) {
      const m = moment(value);
      return m.isValid() ? m.format("YYYY-MM-DD") : "-";
    },
    formatDateTime(value) {
      const m = moment(value);
      return m.isValid() ? m.format("YYYY-MM-DD HH:mm") : "-";
    },
  },
};
</script>

<style scoped>
@import url("./inspectionReportDetail.css");
</style>
